import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import FilterPanel from '../components/FilterPanel';
import LoanInfoModal from '../components/LoanInfoModal';
import AccountingAnalytics from '../components/AccountingAnalytics';
import SystemNotificationPanel from '../components/SystemNotificationPanel';
import SpecifiedSkillNewsPanel, { NewsActionCard } from '../components/SpecifiedSkillNewsPanel';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import { getInitialFilters, getFilterConfigByRole, filtersToApiParams } from '../config/filterConfig';

const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
};

const formatYen = (value) => `${toNumber(value).toLocaleString('ja-JP')}円`;
const ACCOUNTING_TABLE_COLUMN_COUNT = 10;
const ACCOUNTING_EXPORT_PAGE_SIZE = 1000;
const EXCEL_MIME_TYPE = 'application/vnd.ms-excel;charset=utf-8;';
const ACCOUNTING_EXPORT_COLUMNS = [
    { key: 'no', label: 'No.', type: 'number' },
    { key: 'fullName', label: '氏名', type: 'string' },
    { key: 'managementNumber', label: '管理番号', type: 'string' },
    { key: 'nationality', label: '国籍', type: 'string' },
    { key: 'employmentStatus', label: '在職状況', type: 'string' },
    { key: 'salesStaffName', label: '営業担当', type: 'string' },
    { key: 'currentResidenceStatus', label: '在留資格・状況', type: 'string' },
    { key: 'residenceCardNumber', label: '在留カード番号', type: 'string' },
    { key: 'residenceCardExpiry', label: '在留カード有効期限', type: 'string' },
    { key: 'startDate', label: '入職日', type: 'string' },
    { key: 'endDate', label: '退職日', type: 'string' },
    { key: 'loanStatus', label: '貸出状態', type: 'string' },
    { key: 'repaymentStatus', label: '返済状態', type: 'string' },
    { key: 'rental', label: '賃貸借款額（円）', type: 'number' },
    { key: 'rentalRepaid', label: '賃貸返済額（円）', type: 'number' },
    { key: 'rentalRemaining', label: '賃貸残高（円）', type: 'number' },
    { key: 'advance', label: '立替金借款額（円）', type: 'number' },
    { key: 'advanceRepaid', label: '立替金返済額（円）', type: 'number' },
    { key: 'advanceRemaining', label: '立替金残高（円）', type: 'number' },
    { key: 'borrowed', label: '借入合計（円）', type: 'number' },
    { key: 'repaid', label: '返済合計（円）', type: 'number' },
    { key: 'remaining', label: '残額合計（円）', type: 'number' },
    { key: 'lastRepaymentDate', label: '最終返済日', type: 'string' }
];

const toPascalKey = (key) => `${key.charAt(0).toUpperCase()}${key.slice(1)}`;

const getAccountingAmount = (talent, topLevelKey, loanKey) => (
    talent?.[topLevelKey]
    ?? talent?.[toPascalKey(topLevelKey)]
    ?? talent?.loan?.[loanKey]
    ?? talent?.Loan?.[toPascalKey(loanKey)]
    ?? 0
);

const getTalentField = (talent, ...keys) => {
    for (const key of keys) {
        const value = talent?.[key] ?? talent?.[toPascalKey(key)];
        if (value !== undefined && value !== null && value !== '') {
            return value;
        }
    }
    return '';
};

const getNestedTalentField = (talent, objectKey, ...keys) => {
    const source = talent?.[objectKey] ?? talent?.[toPascalKey(objectKey)] ?? {};
    return getTalentField(source, ...keys);
};

const formatDateOnly = (value) => {
    if (!value) return '';
    return String(value).split('T')[0];
};

const escapeXml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const buildExcelCell = (value, type = 'string', styleId = 'Default') => {
    if (type === 'number') {
        return `<Cell ss:StyleID="${styleId}"><Data ss:Type="Number">${toNumber(value)}</Data></Cell>`;
    }

    return `<Cell ss:StyleID="${styleId}"><Data ss:Type="String">${escapeXml(value)}</Data></Cell>`;
};

const buildAccountingExcelXml = (rows) => {
    const headerRow = ACCOUNTING_EXPORT_COLUMNS
        .map(column => buildExcelCell(column.label, 'string', 'Header'))
        .join('');
    const dataRows = rows
        .map(row => `<Row>${ACCOUNTING_EXPORT_COLUMNS
            .map(column => buildExcelCell(row[column.key], column.type, column.type === 'number' ? 'Amount' : 'Default'))
            .join('')}</Row>`)
        .join('');

    return `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="Default">
   <Alignment ss:Vertical="Center"/>
  </Style>
  <Style ss:ID="Header">
   <Font ss:Bold="1"/>
   <Interior ss:Color="#E2E8F0" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
   </Borders>
  </Style>
  <Style ss:ID="Amount">
   <NumberFormat ss:Format="#,##0"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="立替金管理">
  <Table>
   <Row>${headerRow}</Row>
   ${dataRows}
  </Table>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <FreezePanes/>
   <FrozenNoSplit/>
   <SplitHorizontal>1</SplitHorizontal>
   <TopRowBottomPane>1</TopRowBottomPane>
   <ActivePane>2</ActivePane>
  </WorksheetOptions>
 </Worksheet>
</Workbook>`;
};

const downloadExcelXml = (xml, fileName) => {
    const blob = new Blob([xml], { type: EXCEL_MIME_TYPE });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const sumTotals = (rows) => rows.reduce((acc, talent) => ({
    rental: acc.rental + toNumber(talent.rental),
    advance: acc.advance + toNumber(talent.advance),
    borrowed: acc.borrowed + toNumber(talent.borrowed),
    repaid: acc.repaid + toNumber(talent.repaid),
    remaining: acc.remaining + toNumber(talent.remaining)
}), { rental: 0, advance: 0, borrowed: 0, repaid: 0, remaining: 0 });

const normalizeTotals = (totals) => ({
    rental: toNumber(totals?.rental ?? totals?.Rental ?? 0),
    advance: toNumber(totals?.advance ?? totals?.Advance ?? 0),
    borrowed: toNumber(totals?.borrowed ?? totals?.Borrowed ?? 0),
    repaid: toNumber(totals?.repaid ?? totals?.Repaid ?? 0),
    remaining: Math.max(0, toNumber(totals?.remaining ?? totals?.Remaining ?? 0))
});

const mapAccountingRow = (talent) => {
    const borrowed = toNumber(talent?.activeLoanBorrowedAmount ?? talent?.ActiveLoanBorrowedAmount ?? 0);
    const rental = toNumber(getAccountingAmount(talent, 'activeLoanRentalAmount', 'rentalLoanAmount'));
    const advance = toNumber(getAccountingAmount(talent, 'activeLoanAdvanceAmount', 'advanceLoanAmount'));
    const rentalRepaid = toNumber(getAccountingAmount(talent, 'activeLoanRentalRepaidAmount', 'rentalRepaidAmount'));
    const advanceRepaid = toNumber(getAccountingAmount(talent, 'activeLoanAdvanceRepaidAmount', 'advanceRepaidAmount'));
    const repaid = toNumber(talent?.activeLoanRepaidAmount ?? talent?.ActiveLoanRepaidAmount ?? 0);
    const rentalRemaining = Math.max(0, rental - rentalRepaid);
    const advanceRemaining = Math.max(0, advance - advanceRepaid);
    const rawRemaining = toNumber(
        talent?.activeLoanRemainingAmount ?? talent?.ActiveLoanRemainingAmount ?? (borrowed - repaid)
    );
    const remaining = Math.max(0, rawRemaining);
    const hasLoanData = Boolean(talent?.hasLoanData ?? talent?.HasLoanData ?? false);
    const isLoanFullyRepaid = Boolean(talent?.isLoanFullyRepaid ?? talent?.IsLoanFullyRepaid ?? false);

    return {
        ...talent,
        rental,
        advance,
        rentalRepaid,
        advanceRepaid,
        rentalRemaining,
        advanceRemaining,
        borrowed,
        repaid,
        remaining,
        managementNumber: talent?.managementNumber ?? talent?.ManagementNumber ?? '',
        hasLoanData,
        isLoanFullyRepaid,
        salesStaffName: talent?.salesStaff?.username ?? talent?.SalesStaff?.Username ?? '未割当'
    };
};

const buildAccountingExportRows = (rows) => rows.map((talent, index) => {
    const hasLoanData = Boolean(talent.hasLoanData);
    const repaymentStatus = !hasLoanData
        ? '未登録'
        : talent.remaining <= 0
            ? '完全返済済み'
            : talent.repaid > 0
                ? '返済中'
                : '未返済';

    return {
        no: index + 1,
        fullName: talent.fullName ?? talent.FullName ?? '',
        managementNumber: talent.managementNumber || '未登録',
        nationality: getTalentField(talent, 'nationality'),
        employmentStatus: getTalentField(talent, 'employmentStatus'),
        salesStaffName: talent.salesStaffName || '未割当',
        currentResidenceStatus: getTalentField(talent, 'currentResidenceStatus', 'effectiveResidenceStatus')
            || getNestedTalentField(talent, 'residencePermit', 'residenceStatus'),
        residenceCardNumber: getTalentField(talent, 'residenceCardNumber')
            || getNestedTalentField(talent, 'residencePermit', 'residenceCardNumber'),
        residenceCardExpiry: formatDateOnly(getTalentField(talent, 'residenceCardExpiry')
            || getNestedTalentField(talent, 'residencePermit', 'residenceCardExpiry')),
        startDate: formatDateOnly(getNestedTalentField(talent, 'residencePermit', 'startDate')),
        endDate: formatDateOnly(getNestedTalentField(talent, 'residencePermit', 'endDate')),
        loanStatus: hasLoanData ? '貸出登録済み' : '未登録',
        repaymentStatus,
        rental: talent.rental,
        rentalRepaid: talent.rentalRepaid,
        rentalRemaining: talent.rentalRemaining,
        advance: talent.advance,
        advanceRepaid: talent.advanceRepaid,
        advanceRemaining: talent.advanceRemaining,
        borrowed: talent.borrowed,
        repaid: talent.repaid,
        remaining: talent.remaining,
        lastRepaymentDate: formatDateOnly(getTalentField(talent, 'lastRepaymentDate'))
    };
});

const SortableHeaderButton = ({ label, sortKey, sortBy, sortDirection, onSort, align = 'left' }) => {
    const isActive = sortBy === sortKey;
    const icon = isActive ? (sortDirection === 'asc' ? '▲' : '▼') : '↕';
    const alignmentClass = align === 'right' ? 'justify-end' : 'justify-start';

    return (
        <button
            type="button"
            onClick={() => onSort(sortKey)}
            className={`w-full inline-flex items-center ${alignmentClass} gap-1 hover:text-[var(--text-primary)] transition-colors duration-150`}
        >
            <span>{label}</span>
            <span className={isActive ? 'text-[var(--primary-600)]' : 'text-[var(--neutral-400)]'}>
                {icon}
            </span>
        </button>
    );
};

export default function AccountingDashboard() {
    const { t } = useTranslation();
    const [view, setView] = useState('home');

    const [talents, setTalents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(50);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');
    const [overallTotals, setOverallTotals] = useState({ rental: 0, advance: 0, borrowed: 0, repaid: 0, remaining: 0 });
    const [filters, setFilters] = useState(() => {
        const config = getFilterConfigByRole('Accounting');
        return getInitialFilters(config);
    });

    const [selectedTalent, setSelectedTalent] = useState(null);
    const [isLoanModalOpen, setIsLoanModalOpen] = useState(false);

    useEffect(() => {
        const config = getFilterConfigByRole('Accounting');
        setFilters(getInitialFilters(config));
    }, []);

    const fetchTalents = useCallback(async (targetPage = currentPage) => {
        setIsLoading(true);
        try {
            const filterParams = filtersToApiParams(filters);
            const response = await apiService.getTalents({
                searchTerm,
                page: targetPage,
                pageSize,
                sortBy,
                descending: sortDirection === 'desc',
                ...filterParams
            });

            const data = response?.data || response || [];
            const mappedTalents = data.map(mapAccountingRow);
            setTalents(mappedTalents);
            setTotalPages(response?.totalPages || 1);
            setTotalCount(response?.totalCount || data.length || 0);
            setCurrentPage(response?.currentPage || targetPage);
            const totalsPayload = response?.totals ?? response?.Totals;
            setOverallTotals(totalsPayload ? normalizeTotals(totalsPayload) : sumTotals(mappedTalents));
        } catch (error) {
            console.error('Failed to load accounting talents:', error);
            setOverallTotals({ rental: 0, advance: 0, borrowed: 0, repaid: 0, remaining: 0 });
            alert(t('fetchFailed'));
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, filters, pageSize, searchTerm, sortBy, sortDirection, t]);

    useEffect(() => {
        if (view !== 'list') return;
        const timer = setTimeout(() => {
            fetchTalents(currentPage);
        }, 300);
        return () => clearTimeout(timer);
    }, [view, currentPage, fetchTalents]);

    useEffect(() => {
        if (view !== 'list') return;
        setCurrentPage(1);
    }, [searchTerm, filters, sortBy, sortDirection, view]);

    const handleOpenList = () => {
        setView('list');
    };

    const handleOpenUnrepaidList = useCallback(() => {
        const config = getFilterConfigByRole('Accounting');
        const initial = getInitialFilters(config);
        setSearchTerm('');
        setCurrentPage(1);
        setFilters({
            ...initial,
            loanStatus: '貸出登録済み',
            repaymentStatus: '未完済'
        });
        setView('list');
    }, []);

    const handleOpenLoan = (talent) => {
        setSelectedTalent(talent);
        setIsLoanModalOpen(true);
    };

    const handleCloseLoanModal = () => {
        setIsLoanModalOpen(false);
        setSelectedTalent(null);
        if (view === 'list') {
            fetchTalents(currentPage);
        }
    };

    const handleSort = useCallback((nextSortBy) => {
        setSortBy((prevSortBy) => {
            if (prevSortBy === nextSortBy) {
                setSortDirection(prevDirection => (prevDirection === 'asc' ? 'desc' : 'asc'));
                return prevSortBy;
            }
            setSortDirection('asc');
            return nextSortBy;
        });
    }, []);

    const fetchAllAccountingRowsForExport = useCallback(async () => {
        const filterParams = filtersToApiParams(filters);
        let exportPage = 1;
        let exportTotalPages = 1;
        const allRows = [];

        do {
            const response = await apiService.getTalents({
                searchTerm,
                page: exportPage,
                pageSize: ACCOUNTING_EXPORT_PAGE_SIZE,
                sortBy,
                descending: sortDirection === 'desc',
                ...filterParams
            });

            const data = response?.data || response || [];
            allRows.push(...data.map(mapAccountingRow));
            exportTotalPages = Math.max(1, response?.totalPages || 1);
            exportPage += 1;
        } while (exportPage <= exportTotalPages);

        return allRows;
    }, [filters, searchTerm, sortBy, sortDirection]);

    const handleExportExcel = useCallback(async () => {
        setIsExporting(true);
        try {
            const exportRows = await fetchAllAccountingRowsForExport();
            const excelRows = buildAccountingExportRows(exportRows);
            const xml = buildAccountingExcelXml(excelRows);
            const dateStamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            downloadExcelXml(xml, `立替金管理_${dateStamp}.xls`);
            alert(t('exportExcelSuccess'));
        } catch (error) {
            console.error('Accounting Excel export failed:', error);
            alert(`${t('exportExcelFailed')}: ${error.message}`);
        } finally {
            setIsExporting(false);
        }
    }, [fetchAllAccountingRowsForExport, t]);

    const pageTotals = useMemo(() => {
        return sumTotals(talents);
    }, [talents]);

    const actions = view !== 'home' ? (
        <div className="w-full sm:w-auto">
            <button
                onClick={() => setView('home')}
                className="px-3 py-2 text-xs sm:text-sm font-semibold rounded-lg shadow-md bg-purple-600 text-white hover:bg-purple-700 min-h-[44px] flex items-center justify-center"
            >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
                {t('backToHome')}
            </button>
        </div>
    ) : null;

    return (
        <>
            <LoanInfoModal
                isOpen={isLoanModalOpen}
                onClose={handleCloseLoanModal}
                talent={selectedTalent}
            />

            <Layout title={t('accountingManagement')} actions={actions} fullWidth={view === 'list' || view === 'news'}>
                {view === 'home' && (
                    <div className="space-y-8">
                        <div className="section-header">
                            <div className="section-header-icon">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="section-header-title">クイックアクション</h2>
                                <p className="section-header-subtitle">よく使う機能にすばやくアクセス</p>
                            </div>
                        </div>

                        <div className="dashboard-bento-grid">
                            <button onClick={handleOpenList} className="action-card action-card--green">
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('registerUpdateLoan')}</span>
                                <span className="action-card-subtitle">貸付一覧を表示</span>
                            </button>
                            <NewsActionCard onClick={() => setView('news')} />
                        </div>

                        <SystemNotificationPanel />
                        <AccountingAnalytics onOpenUnrepaidList={handleOpenUnrepaidList} />
                    </div>
                )}

                {view === 'list' && (
                    <div className="flex h-screen">
                        <FilterPanel
                            role="Accounting"
                            filters={filters}
                            onFiltersChange={setFilters}
                        />

                        <div className="flex-1 overflow-auto">
                            <div className="mb-4 bg-[var(--surface)] p-4 rounded-lg shadow-[var(--shadow-sm)] border border-[var(--border)]">
                                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="text-sm text-[var(--text-secondary)]">
                                        {t('totalRecords')}: {totalCount}件 | {t('page')} {currentPage} / {totalPages}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleExportExcel}
                                        disabled={isExporting}
                                        className="inline-flex min-h-[38px] items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        {isExporting ? t('exportingExcel') : t('exportExcel')}
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    placeholder={t('searchNameNumber')}
                                    className="w-full px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary-200)] focus:border-[var(--primary-500)] transition-all duration-200"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="bg-[var(--surface)] rounded-lg shadow-[var(--shadow-sm)] border border-[var(--border)] overflow-x-auto">
                                <table className="min-w-full divide-y divide-[var(--border)]">
                                    <thead className="bg-[var(--neutral-50)]">
                                        <tr>
                                            <th rowSpan={2} className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">No.</th>
                                            <th rowSpan={2} className="px-6 py-3 text-left text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                                                <SortableHeaderButton
                                                    label={t('name')}
                                                    sortKey="fullName"
                                                    sortBy={sortBy}
                                                    sortDirection={sortDirection}
                                                    onSort={handleSort}
                                                />
                                            </th>
                                            <th rowSpan={2} className="px-6 py-3 text-left text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                                                <SortableHeaderButton
                                                    label={t('managementNumber')}
                                                    sortKey="managementNumber"
                                                    sortBy={sortBy}
                                                    sortDirection={sortDirection}
                                                    onSort={handleSort}
                                                />
                                            </th>
                                            <th rowSpan={2} className="px-6 py-3 text-left text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                                                <SortableHeaderButton
                                                    label="営業担当"
                                                    sortKey="salesStaff"
                                                    sortBy={sortBy}
                                                    sortDirection={sortDirection}
                                                    onSort={handleSort}
                                                />
                                            </th>
                                            <th className="px-6 py-2 text-right text-xs font-semibold text-[var(--text-secondary)]">
                                                <div className="flex flex-col items-end leading-tight">
                                                    <span className="text-[13px] font-bold text-[var(--text-primary)]">全件賃貸: {formatYen(overallTotals.rental)}</span>
                                                    <span className="text-[10px] font-medium text-[var(--text-muted)]">本ページ賃貸: {formatYen(pageTotals.rental)}</span>
                                                </div>
                                            </th>
                                            <th className="px-6 py-2 text-right text-xs font-semibold text-[var(--text-secondary)]">
                                                <div className="flex flex-col items-end leading-tight">
                                                    <span className="text-[13px] font-bold text-[var(--text-primary)]">全件立替金: {formatYen(overallTotals.advance)}</span>
                                                    <span className="text-[10px] font-medium text-[var(--text-muted)]">本ページ立替金: {formatYen(pageTotals.advance)}</span>
                                                </div>
                                            </th>
                                            <th className="px-6 py-2 text-right text-xs font-semibold text-[var(--text-secondary)]">
                                                <div className="flex flex-col items-end leading-tight">
                                                    <span className="text-[13px] font-bold text-[var(--text-primary)]">全件借入合計: {formatYen(overallTotals.borrowed)}</span>
                                                    <span className="text-[10px] font-medium text-[var(--text-muted)]">本ページ借入合計: {formatYen(pageTotals.borrowed)}</span>
                                                </div>
                                            </th>
                                            <th className="px-6 py-2 text-right text-xs font-semibold text-[var(--text-secondary)]">
                                                <div className="flex flex-col items-end leading-tight">
                                                    <span className="text-[13px] font-bold text-[var(--text-primary)]">全件返済合計: {formatYen(overallTotals.repaid)}</span>
                                                    <span className="text-[10px] font-medium text-[var(--text-muted)]">本ページ返済合計: {formatYen(pageTotals.repaid)}</span>
                                                </div>
                                            </th>
                                            <th className="px-6 py-2 text-right text-xs font-semibold text-[var(--text-secondary)]">
                                                <div className="flex flex-col items-end leading-tight">
                                                    <span className="text-[13px] font-bold text-[var(--text-primary)]">全件残額合計: {formatYen(overallTotals.remaining)}</span>
                                                    <span className="text-[10px] font-medium text-[var(--text-muted)]">本ページ残額合計: {formatYen(pageTotals.remaining)}</span>
                                                </div>
                                            </th>
                                            <th rowSpan={2} className="px-6 py-3 text-right text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('actions')}</th>
                                        </tr>
                                        <tr>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                                                <SortableHeaderButton
                                                    label="賃貸"
                                                    sortKey="activeLoanRentalAmount"
                                                    sortBy={sortBy}
                                                    sortDirection={sortDirection}
                                                    onSort={handleSort}
                                                    align="right"
                                                />
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                                                <SortableHeaderButton
                                                    label="立替金"
                                                    sortKey="activeLoanAdvanceAmount"
                                                    sortBy={sortBy}
                                                    sortDirection={sortDirection}
                                                    onSort={handleSort}
                                                    align="right"
                                                />
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                                                <SortableHeaderButton
                                                    label="借入"
                                                    sortKey="activeLoanBorrowedAmount"
                                                    sortBy={sortBy}
                                                    sortDirection={sortDirection}
                                                    onSort={handleSort}
                                                    align="right"
                                                />
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                                                <SortableHeaderButton
                                                    label="返済"
                                                    sortKey="activeLoanRepaidAmount"
                                                    sortBy={sortBy}
                                                    sortDirection={sortDirection}
                                                    onSort={handleSort}
                                                    align="right"
                                                />
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                                                <SortableHeaderButton
                                                    label="残額"
                                                    sortKey="activeLoanRemainingAmount"
                                                    sortBy={sortBy}
                                                    sortDirection={sortDirection}
                                                    onSort={handleSort}
                                                    align="right"
                                                />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-[var(--surface)] divide-y divide-[var(--border)]">
                                        {isLoading && (
                                            <tr>
                                                <td colSpan={ACCOUNTING_TABLE_COLUMN_COUNT} className="px-6 py-10 text-center text-sm text-[var(--text-muted)]">
                                                    {t('loading')}
                                                </td>
                                            </tr>
                                        )}

                                        {!isLoading && talents.length === 0 && (
                                            <tr>
                                                <td colSpan={ACCOUNTING_TABLE_COLUMN_COUNT} className="px-6 py-10 text-center text-sm text-[var(--text-muted)]">
                                                    {t('noTalentDataFound')}
                                                </td>
                                            </tr>
                                        )}

                                        {!isLoading && talents.map((talent, index) => (
                                            <tr key={talent.id} className="hover:bg-[var(--neutral-50)] transition-colors duration-150">
                                                <td className="px-4 py-4 text-sm text-[var(--text-secondary)]">
                                                    <span className="inline-flex items-center justify-center w-8 h-8 bg-[var(--neutral-100)] text-[var(--text-secondary)] rounded-full text-xs font-semibold">
                                                        {(currentPage - 1) * pageSize + index + 1}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm font-medium text-[var(--text-primary)]">{talent.fullName}</td>
                                                <td className="px-6 py-4 text-sm text-[var(--text-secondary)]">{talent.managementNumber || t('notRegistered')}</td>
                                                <td className="px-6 py-4 text-sm text-[var(--text-secondary)]">{talent.salesStaffName}</td>
                                                <td className="px-6 py-4 text-sm text-right text-[var(--text-secondary)]">{formatYen(talent.rental)}</td>
                                                <td className="px-6 py-4 text-sm text-right text-[var(--text-secondary)]">{formatYen(talent.advance)}</td>
                                                <td className="px-6 py-4 text-sm text-right text-[var(--text-secondary)]">{formatYen(talent.borrowed)}</td>
                                                <td className="px-6 py-4 text-sm text-right text-[var(--text-secondary)]">{formatYen(talent.repaid)}</td>
                                                <td className="px-6 py-4 text-sm text-right text-[var(--text-secondary)]">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <span>{formatYen(talent.remaining)}</span>
                                                        {!talent.hasLoanData && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700">
                                                                {t('loanNotRegistered')}
                                                            </span>
                                                        )}
                                                        {talent.hasLoanData && talent.isLoanFullyRepaid && talent.remaining <= 0 && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700">
                                                                {t('loanFullyRepaid')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <button
                                                        onClick={() => handleOpenLoan(talent)}
                                                        className="px-3 py-1.5 text-xs font-medium text-[var(--primary-700)] bg-[var(--primary-50)] hover:bg-[var(--primary-100)] border border-[var(--primary-200)] rounded-md transition-all duration-200 hover:shadow-sm"
                                                    >
                                                        {t('viewEdit')}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="mt-4 bg-white p-4 rounded-lg shadow">
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div className="text-sm text-gray-600">
                                            {t('showingResults', {
                                                start: (currentPage - 1) * pageSize + 1,
                                                end: Math.min(currentPage * pageSize, totalCount),
                                                total: totalCount
                                            })}
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('firstPage')}
                                            </button>

                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentPage === 1}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('previousPage')}
                                            </button>

                                            <span className="px-4 py-2 text-sm text-gray-700">
                                                {t('pageInfo', { current: currentPage, total: totalPages })}
                                            </span>

                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('nextPage')}
                                            </button>

                                            <button
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('lastPage')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {view === 'news' && <SpecifiedSkillNewsPanel />}
            </Layout>
        </>
    );
}
